import React, { ChangeEvent, useEffect, useState, useCallback } from "react";
import { FieldErrors, useForm } from "react-hook-form";

// Test Comment

// Material-UI components
import {
  Box,
  Grid,
  TextField,
  FormControl,
  Button,
  MenuItem,
  Select,
  SelectChangeEvent, Typography, Card,
  CardContent,Divider,
  Tabs,
  Tab,
} from "@mui/material";

// Internal dependencies
import Spinner from "components/Spinner";
import { AlertsProps } from "components/Alerts/data/Alerts.type";
import { orgTypes, annualRevenueRanges } from "./orgTypes";
import { _addOrganization, _updateOrganization } from "middlewares/OrganizationApi/organization";
import masterPropertyApi from "middlewares/MasterProperty";

import { useDropzone } from "react-dropzone";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { Organization } from "../types";
import OrgSetTarget from "./OrgSetTarget";

// Interface for the props of the AddClient component
interface AddOrganizationProps {
  handleCloseSnackBar: (state: AlertsProps) => void;
  hideDialogHandler: () => void;
  clientId?: number;
  rowData?: Organization | null;
  initialTab?: number;
  onOrganizationSaved?: (org: Organization, isEdit: boolean) => void;
}

/**
 * AddOrganization component
 *
 * This component renders a form for adding or updating a user. It handles form submission, input changes, and API calls for saving user data.
 *
 * @param {AddOrganizationProps} props - The props for the component.
 * @param {Function} props.handleCloseSnackBar - Function to handle closing the snackbar.
 * @param {Function} props.hideDialogHandler - Function to hide the dialog.
 * @returns {JSX.Element} A form for adding or updating a client.
 */
const AddOrganization = (props: AddOrganizationProps) => {

    // State for user data and loading status
    const [organization, setOrganization] = useState<Organization & { imageFile?: File }>({
        orgId: 0,
        clientId: 1,
        orgName: "",
        orgType: "",
        industry: "",
        description: "",
        annualRevenue: "",
        imageName: "",
        locations: [],
    });

    const [loading, setLoading] = useState(false);
    const {
        register,
        handleSubmit,
        setValue,
        formState: { errors },
        trigger,
      } = useForm({ mode: "onSubmit", shouldUnregister: false });

    const [industryList, setIndustryList] = useState<
        Array<{ id: string; value: string, label?: string }>
        >([]);

    const [activeTab, setActiveTab] = useState(props.initialTab ?? 0);
    const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
        setActiveTab(newValue);
    };
    const isEditMode = organization.orgId > 0;
    const orgImageTabIndex = 2;
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [base64Image, setBase64Image] = useState<string | null>(null);

    useEffect(() => {
        if (props) {
            setOrganization({
                orgId: props.rowData?.orgId ?? 0,
                orgName: props.rowData?.orgName ?? "",
                orgType: props.rowData?.orgType ?? "",
                industry: props.rowData?.industry ?? "",
                annualRevenue: props.rowData?.annualRevenue ?? "",
                clientId: props.rowData?.clientId ?? props.clientId ?? 0,
                description: props.rowData?.description ?? "",
                createdBy: props.rowData?.createdBy ?? "",
                createdAt:
                    typeof props.rowData?.createdAt === "string"
                        ? new Date(props.rowData.createdAt)
                        : props.rowData?.createdAt,
                locations: props.rowData?.locations ?? [],
                imageName: props.rowData?.imageName ?? "",
            });
            setValue("orgName", props.rowData?.orgName);
            setValue("industry", props.rowData?.industry);
            setValue("description", props.rowData?.description);
            setValue("annualRevenue", props.rowData?.annualRevenue);
            setValue("orgType", props.rowData?.orgType);

            if (props.rowData?.imageName) {
                setBase64Image(props.rowData.imageName);
                try {
                    const file = convertBase64ToFile(props.rowData.imageName, "org-image");
                    setSelectedFile(file);
                    setOrganization(prev => ({ ...prev, imageFile: file }));
                } catch (err) {
                    console.error("Failed to convert base64 image to File:", err);
        }
            }
        }
        getIndustryList();
    }, [props.rowData, setValue]);

    useEffect(() => {
        if (props.clientId) { 
            setOrganization(prev => ({ ...prev, clientId: props.clientId ?? 0 }));
        }
    }, [props.clientId]);

    /**
     * Handles the file drop event from the dropzone and starts base64 conversion.
     *
     * @param {File[]} acceptedFiles - Array of dropped files.
     */
    const onDrop = useCallback((acceptedFiles: File[]) => {
        if (acceptedFiles.length > 0) {
            convertToBase64(acceptedFiles[0]);
            setSelectedFile(acceptedFiles[0]);
            setOrganization(prev => ({ ...prev, imageFile: acceptedFiles[0] }));
        }
    }, []);

    const convertToBase64 = (file: File) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
            setBase64Image(reader.result as string);
            setOrganization(prev => ({ ...prev, image: (reader.result as string) ? (reader.result as string) : prev.imageName }));
        };
        reader.onerror = (error) => console.error("Error converting file:", error);
    };

    function convertBase64ToFile(base64Data: string, fileName: string): File {
        const match = /^data:(.+);base64,(.*)$/.exec(base64Data);
        let mime = "image/png";
        let b64 = base64Data;
        if (match) {
            mime = match[1];
            b64 = match[2];
        }
        const byteChars = atob(b64);
        const byteNumbers = new Array(byteChars.length);
        for (let i = 0; i < byteChars.length; i++) {
            byteNumbers[i] = byteChars.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: mime });
        return new File([blob], fileName, { type: mime });
    }

    function getIndustryList() {
        masterPropertyApi
            .getMasterPropertyListByCategoryAndType("Location", "Industry")
            .then((res) => {
                if (!res || res.length === 0) {
                    return;
                }
                const list = res.map(item => ({
                    id: String(item.id ?? ""),   // convert to string, fallback empty string
                    value: String(item.id ?? ""),
                    label: item.value
                }));

                setIndustryList(list);
                setLoading(false);
            });
    }

    // Handle input change for text fields
    const handleInputChange = (
        e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent
        ) => {
        const { name, value } = e.target;

        setOrganization((prev) => ({
            ...prev,
            [name]: name === "AnnualRevenue" ? Number(value) : value
        }));
        };

    const handleSelectInputChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent
        ) => {
        let name: string;
        let value: string;
        let textLabel: string = "";

        // If the event is fired by MUI Select
        if ("target" in e && typeof e.target.value === "string") {
            name = e.target.name!;
            value = e.target.value.toString();
            
            // Get the text/label from the selected option
            if (name === "orgType") {
                const selectedType = orgTypes.find(t => t.value === value);
                textLabel = selectedType?.label ?? value;
            } else if (name === "industry") {
                const selectedIndustry = industryList.find(i => i.value === value);
                textLabel = selectedIndustry?.label ?? value;
            } else if (name === "annualRevenue") {
                const selectedRevenue = annualRevenueRanges.find(r => r.value === value);
                textLabel = selectedRevenue?.label ?? value;
            }
        } else {
            // TextField or TextArea
            const target = e.target as HTMLInputElement | HTMLTextAreaElement;
            name = target.name;
            value = target.value;
            textLabel = value;
        }

        setOrganization((prev) => ({
            ...prev,
            [name]: textLabel || value,
        }));
        
        // Update react-hook-form value for validation
        setValue(name, textLabel || value, { shouldValidate: false });
    };



        
    useEffect(() => {
        if (panelHasErrors("panel1", errors)) {
            setActiveTab(0);
        } 
    }, [errors]);

    const PANEL_FIELD_MAP: Record<string, string[]> = {
            panel1: ["orgName", "orgType", "industry", "annualRevenue"],
    };

    const panelHasErrors = (
        panel: keyof typeof PANEL_FIELD_MAP,
        errors: FieldErrors
        ): boolean => {
        return PANEL_FIELD_MAP[panel].some((field) => !!errors[field]);
            };
    
    /**
     * Handles saving or updating a user by calling the appropriate API function.
     *
     * @param {Function} apiFunction - The API function to call (_addUser or _updateUser).
     */
    const handleSaveOrUpdate = async (apiFunction: Function) => {
        if (typeof apiFunction === "function") {
            try {
        setLoading(true);
                const formData = new FormData();
                formData.append("OrgID", String(organization.orgId ?? 0));
                formData.append("OrgName", organization.orgName ?? "");
                formData.append("Industry", organization.industry ?? "");
                formData.append("Description", organization.description ?? "");
                formData.append("AnnualRevenue", organization.annualRevenue ?? "");
                formData.append("OrgType", organization.orgType ?? "");
                formData.append("ClientID", organization.clientId?.toString() ?? "0");

                if (organization.imageFile) {
                    formData.append("ImageName", organization.imageFile ?? "");
                }
                
                let res;
                if (organization.orgId > 0) {
                    res = await _updateOrganization(formData);
                } else {
                    res = await _addOrganization(formData);
                }
                
            if (res) {
                    
                const message =
                    organization.orgId === 0
                        ? `${organization.orgName} Created Successfully !`
                    : "Data Saved Successfully !";
                    setLoading(false);
                props.handleCloseSnackBar({
                    show: true,
                    severity: "success",
                    message: message,
                });
                props.onOrganizationSaved?.(organization, organization.orgId > 0);
                props.hideDialogHandler();
            }
            } catch (err) {
                console.error(err)
                setLoading(false);
                props.handleCloseSnackBar({
                    show: true,
                    severity: "error",
                    message: "Something went wrong. Please try again.",
                });
                props.hideDialogHandler();
            } finally {
                    setLoading(false);
                }
        }
    };

    const { getRootProps, getInputProps } = useDropzone({
        onDrop,
        accept: { "image/*": [] }, // Accept only images
        maxSize: 10 * 1024 * 1024, // 10MB limit
        multiple: false, // Allow only one file at a time
        });

    const onValidSubmit = () => {
        handleSaveOrUpdate(organization.orgId > 0 ? _updateOrganization : _addOrganization);
    };  

    const onInvalidSubmit = async (formErrors: FieldErrors) => {
        await trigger();
        expandAccordionOnError(formErrors);

        const firstErrorField = Object.keys(formErrors)[0];

        setTimeout(() => {
            const el = document.querySelector(
            `[name="${firstErrorField}"]`
            ) as HTMLElement | null;

            el?.focus();
        }, 150);
    };

    const expandAccordionOnError = (formErrors: FieldErrors) => {
        if (
            formErrors.orgName ||
            formErrors.orgType ||
            formErrors.industry ||
            formErrors.annualRevenue

        ) {
            setActiveTab(0);
        }
    };

      // JSX structure for the AddUser component
    return (
        <Box sx={{ mx: 2, my: 1 }}>
        <form onSubmit={handleSubmit(onValidSubmit, onInvalidSubmit)} noValidate data-testid="form">
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange}
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '0.95rem',
              minWidth: 'auto',
              px: 3,
            },
            '& .Mui-selected': {
              color: 'primary.main',
            },
            '& .MuiTabs-indicator': {
              backgroundColor: 'primary.main',
            },
          }}
        >
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                General Information
                {panelHasErrors("panel1", errors) && (
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: '#E4610F',
                    }}
                  />
                )}
              </Box>
            }
          />
          <Tab label="Set Target" />
          <Tab label="Organization Image" />
        </Tabs>
      </Box>

            {/* Tab Panel 1 - General Information (always rendered, hidden if inactive) */}
            <Box
                sx={{
                    py: 2,
                    display: activeTab === 0 ? 'block' : 'none',
                }}
            >
            <Grid container spacing={1.5}>
                <Grid item xs={12} sx={{ mb: 1 }}>
                    <TextField
                        inputProps={{ "data-testid": "OrgName" }}
                        {...register("orgName", {
                            required: "OrgName is required",
                            validate: (value) => {
                            const orgName = value?.trim();
                            if (orgName.length === 0) {
                                return "Enter a OrgName";
                            }
                            return true;
                            },
                        })}
                        name="orgName"
                        value={organization.orgName ?? ""}
                        size="small"
                        label="Organization Name *"
                        fullWidth
                        onChange={handleInputChange}
                        error={!!errors.orgName}
                            helperText={errors.orgName?.message as string}
                        sx={{
                            '& .MuiInputBase-root': { fontSize: '0.813rem', height: '36px' },
                            '& .MuiInputLabel-root': { fontSize: '0.813rem' },
                            '& .MuiFormHelperText-root': { fontSize: '0.7rem', mt: 0.25 }
                        }}
                    />
                </Grid>
                <Grid item xs={12} sx={{ mb: 1 }}>
                    <FormControl fullWidth error={!!errors.orgType}>
                        <Select
                            data-testid="OrgType"
                            {...register("orgType", {
                                required: "Organization Type is required",
                                validate: (value) => {
                                    if (!value || value === "") {
                                        return "Please select an Organization Type";
                                    }
                                    return true;
                                },
                            })}
                            name="orgType"
                            value={orgTypes.find(t => t.label === organization.orgType)?.value ?? ""}
                            onChange={handleSelectInputChange}
                            displayEmpty
                            size="small"
                            fullWidth
                            sx={{
                                fontSize: '0.813rem',
                                height: '36px',
                                '& .MuiSelect-select': { py: 0.75 }
                            }}
                            >
                            <MenuItem value="" sx={{ fontSize: '0.813rem' }}>Select Organization Type *</MenuItem>
                            {orgTypes.map((t) => (
                                <MenuItem key={t.id} value={t.value} sx={{ fontSize: '0.813rem' }}>
                                {t.label}
                                </MenuItem>
                            ))}
                        </Select>
                        {errors.orgType && (
                            <Typography 
                                sx={{ 
                                    fontSize: '0.7rem', 
                                    mt: 0.25, 
                                    ml: 1.75,
                                    color: 'error.main' 
                                }}
                            >
                                {errors.orgType?.message as string}
                            </Typography>
                        )}
                    </FormControl>
                </Grid>
                <Grid item xs={12} sx={{ mb: 2 }}>
                    <TextField
                        inputProps={{ "data-testid": "Description" }}
                        {...register("description", {
                            required: false,
                        })}
                        name="description"
                        value={organization.description ?? ""}
                        size="small"
                        label="Description"
                        fullWidth
                        onChange={handleInputChange}
                        sx={{
                            '& .MuiInputBase-root': { fontSize: '0.813rem', height: '36px' },
                            '& .MuiInputLabel-root': { fontSize: '0.813rem' }
                        }}
                    />
                </Grid>
                <Grid item xs={12} sx={{ mb: 1 }}>
                    <FormControl fullWidth size="small" error={!!errors.industry}>
                        <Select
                            data-testid="Industry"
                            {...register("industry", {
                                required: "Industry is required",
                                validate: (value) => {
                                    if (!value || value === "") {
                                        return "Please select an Industry";
                                    }
                                    return true;
                                },
                            })}
                            name="industry"
                            value={industryList.find(i => i.label === organization.industry)?.value ?? ""}
                            onChange={handleSelectInputChange}
                            displayEmpty
                            size="small"
                            fullWidth
                            sx={{
                                fontSize: '0.813rem',
                                height: '36px',
                                '& .MuiSelect-select': { py: 0.75 }
                            }}
                            >
                            <MenuItem value="" sx={{ fontSize: '0.813rem' }}>Select Industry *</MenuItem>
                            {industryList.map((t) => (
                                <MenuItem key={t.id} value={t.value} sx={{ fontSize: '0.813rem' }}>
                                {t.label}
                                </MenuItem>
                            ))}
                        </Select>
                        {errors.industry && (
                            <Typography 
                                sx={{ 
                                    fontSize: '0.7rem', 
                                    mt: 0.25, 
                                    ml: 1.75,
                                    color: 'error.main' 
                                }}
                            >
                                {errors.industry?.message as string}
                            </Typography>
                        )}
                    </FormControl>
                </Grid>
                <Grid item xs={12} sx={{ mb: 1 }}>
                    <FormControl fullWidth size="small" error={!!errors.annualRevenue}>
                        <Select
                            data-testid="AnnualRevenue"
                            {...register("annualRevenue", {
                                required: "Annual Revenue is required",
                                validate: (value) => {
                                    if (!value || value === "") {
                                        return "Please select an Annual Revenue";
                                    }
                                    return true;
                                },
                            })}
                        name="annualRevenue"
                            value={annualRevenueRanges.find(r => r.label === organization.annualRevenue)?.value ?? ""}
                            onChange={handleSelectInputChange}
                            displayEmpty
                            size="small"
                            fullWidth
                            sx={{
                                fontSize: '0.813rem',
                                height: '36px',
                                '& .MuiSelect-select': { py: 0.75 }
                            }}
                            >
                            <MenuItem value="" sx={{ fontSize: '0.813rem' }}>Select Annual Revenue *</MenuItem>
                            {annualRevenueRanges.map((t) => (
                                <MenuItem key={t.id} value={t.value} sx={{ fontSize: '0.813rem' }}>
                                {t.label}
                                </MenuItem>
                            ))}
                        </Select>
                        {errors.annualRevenue && (
                            <Typography 
                                sx={{ 
                                    fontSize: '0.7rem', 
                                    mt: 0.25, 
                                    ml: 1.75,
                                    color: 'error.main' 
                                }}
                            >
                                {errors.annualRevenue?.message as string}
                            </Typography>
                        )}
                    </FormControl>
                </Grid>
            </Grid>
        </Box>

            {/* Tab Panel - Set Target */}
            <Box sx={{ py: 2, display: activeTab === 1 ? 'block' : 'none' }}>
              {isEditMode ? (
                <OrgSetTarget
                  orgId={organization.orgId}
                  handleCloseSnackBar={props.handleCloseSnackBar}
                />
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: 120,
                    color: 'text.secondary',
                    gap: 1,
                  }}
                >
                  <Typography variant="body2" textAlign="center">
                    This section will be available once the organization has been successfully created.
                  </Typography>
                  <Typography sx={{ fontSize: '0.62rem', color: 'text.secondary', lineHeight: 1.3, fontStyle: 'italic' }}>
                    <strong>Note:</strong> Targets are used for GHG reduction tracking across all locations.
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Tab Panel 2 - Organization Image (always rendered, hidden if inactive) */}
            <Box
                sx={{
                    py: 2,
                    display: activeTab === orgImageTabIndex ? 'block' : 'none',
                }}
            >
            <Grid container spacing={2} alignItems="center">
            {/* Image Preview */}
            <Grid item xs={12} sm={12}>
                <Typography variant="body1" textAlign="center" sx={{ mb: 0 }}>
                    Upload an image for your organization.
                </Typography>
                <Box sx={{ maxWidth: 600, margin: "auto", mt: 0 }}>
                    <Card elevation={3}>
                <CardContent>
                    {base64Image && (
                        <>
                        <Box
                            component="img"
                            src={base64Image}
                            alt="Uploaded preview"
                            sx={{
                            display: "block",
                            margin: "auto",
                            maxWidth: "100%",
                                            maxHeight: 180,
                            borderRadius: 1,
                            }}
                        />
                                        <Divider sx={{ my: 2 }} />
                        </>
                    )}
                            <Box
                                {...getRootProps()}
                                sx={{
                                    border: "2px dashed #ccc",
                                    borderRadius: 2,
                                    padding: 3,
                                    textAlign: "center",
                                    cursor: "pointer",
                                    backgroundColor: "#f9f9f9",
                                    "&:hover": { backgroundColor: "#f1f1f1" },
                                }}
                                data-testid="dropzone"
                                >
                                <input {...getInputProps()} />
                                    <CloudUploadIcon sx={{ fontSize: 40, color: "orange" }} />
                                    <Typography variant="body1" sx={{ mt: 1 }}>
                                            {selectedFile?.name ?? "Drag and drop a file here"}
                                    </Typography>
                                    <Button
                                        variant="outlined"
                                        color="warning"
                                        sx={{ mt: 2, textTransform: "none" }}
                                    >
                                        Browse Image
                                    </Button>
                                    <Typography
                                        variant="caption"
                                        display="block"
                                        sx={{ mt: 1, color: "gray" }}
                                    >
                                        Maximum file size is 10 MB
                                    </Typography>
                                </Box>
                </CardContent>
            </Card>
                </Box>
            </Grid>
            </Grid>
        </Box>
        
        <Box sx={{ position: 'absolute', bottom: 16, right: 16, display: activeTab === 1 ? 'none' : 'flex', justifyContent: 'flex-end', zIndex: 10 }}>
            <Button 
                type="submit" 
                variant="contained" 
                data-testid="btnSave"
                size="small"
                sx={{
                    textTransform: 'none',
                    fontSize: '0.75rem',
                    py: 0.25,
                    px: 1,
                    minHeight: 'auto',
                    mb: 1,
                    mr: 1,
                }}
            >
                {organization.orgId > 0 ? 'Update Organization' : 'Save Organization'}
            </Button>
        </Box>
        </form>
        {loading && <Spinner size={80} data-testid="spinner" />}
        </Box>
    );
    };
      
export default AddOrganization;